// AdminContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import checkAdminStatus from '../api/Auth/checkAdminStatus';
import { getOrders } from '../modules/admin/modules/orders/orders.api';
import { useSelector } from 'react-redux';
import { authSelector } from 'redux/reducers/auth';

interface AdminContextType {
    isAdmin: boolean;
    adminLoaded: boolean;
    pendingOrders: number;
    pendingMarketplaceResellRequests: number;
    pendingSubscriptionChangeRequests: number;
}

const AdminContext = createContext<AdminContextType>({
    isAdmin: false,
    adminLoaded: false,
    pendingOrders: 0,
    pendingMarketplaceResellRequests: 0,
    pendingSubscriptionChangeRequests: 0,
});

interface AdminProviderProps {
    children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
    const [adminData, setAdminData] = useState<AdminContextType>({
        isAdmin: false,
        adminLoaded: false,
        pendingOrders: 0,
        pendingMarketplaceResellRequests: 0,
        pendingSubscriptionChangeRequests: 0,
    });
    const { isAuth } = useSelector(authSelector);

    useEffect(() => {
        const fetchAdminStatus = async () => {
            try {
                if (window.location.href.includes('/unauthorized')) {
                    setAdminData((prev) => ({ ...prev, adminLoaded: true }));
                    return;
                }

                const response = await checkAdminStatus();
                const isAdmin = response.status === 200 && response.data.is_global_admin;

                let pendingOrders = 0;
                if (isAdmin) {
                    try {
                        const ordersRes = await getOrders({
                            order_status: 'processing',
                            per_page: 1,
                        });
                        pendingOrders = ordersRes.data?.meta?.total ?? 0;
                    } catch {
                        pendingOrders = 0;
                    }
                }

                setAdminData({
                    isAdmin,
                    adminLoaded: true,
                    pendingOrders,
                    pendingMarketplaceResellRequests:
                        response.data.notifications.pending_marketplace_resell_requests,
                    pendingSubscriptionChangeRequests:
                        response.data.notifications.pending_subscription_change_requests,
                });
            } catch (error) {
                setAdminData({
                    isAdmin: false,
                    adminLoaded: true,
                    pendingOrders: 0,
                    pendingMarketplaceResellRequests: 0,
                    pendingSubscriptionChangeRequests: 0,
                });
            }
        };

        if (isAuth) {
            void fetchAdminStatus();
        } else {
            setAdminData((prev) => ({ ...prev, adminLoaded: true }));
        }
    }, [isAuth]);

    return <AdminContext.Provider value={adminData}>{children}</AdminContext.Provider>;
};

export const UseAdminStatus = () => useContext(AdminContext);
